import React, { useState } from 'react';
import { Button, notification, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { Content } from 'antd/lib/layout/layout';

import { useAppDispatch, useAppSelector } from 'store';
import { MainLayout } from 'components/Layout/MainLayout';
import { useQuery } from 'hooks/useQuery';
import { useDeepEffect } from 'utils/useDeepEffect';
import { StickyConfirmModal } from 'components/modals/StickyConfirmModal';
import {
  loadRentalsList,
  setUnPublishRentalsItem,
  deleteRentalsItem,
} from 'store/slices/rentals/actions';
import { PageTitles } from 'constants/pageTitles';
import { StatusesEnum } from 'constants/status';
import { canBeUnPublished } from 'utils/rights';
import { IRentalsItemResponse } from 'store/slices/rentals/interfaces';

import { Filters } from './components/Filters';
import { ListTable } from './components/ListTable';

export const List = () => {
  const [sort, setSort] = React.useState(null);
  const dispatch = useAppDispatch();
  const query = useQuery();

  const { list, total, limit } = useAppSelector(state => state.rentals);
  const { b2bUrl } = useAppSelector(state => state.configSlice);

  const loadRentals = () => {
    dispatch(
      loadRentalsList({
        ...query,
        sort,
      })
    );
  };

  const [modalIsOpen, setModalIsOpen] = useState(null);
  const [currentItem, setCurrentItem] = useState(null);

  useDeepEffect(() => {
    loadRentals();
  }, [query, sort]);

  const modalCloseHandler = () => {
    setModalIsOpen(null);
    setCurrentItem(null);
  };

  const handleUnPublish = async id => {
    await dispatch(setUnPublishRentalsItem(id))
      .unwrap()
      .then(() => {
        notification.success({
          message: 'Карточка объекта аренды успешно снята с публикации',
        });
        loadRentals();
        modalCloseHandler();
      });
  };

  const handleDelete = async id => {
    await dispatch(deleteRentalsItem(id))
      .unwrap()
      .then(() => {
        notification.success({
          message: 'Карточка объекта аренды успешно перемещена в Корзину',
        });
        loadRentals();
        modalCloseHandler();
      });
  };

  const getActions = (item: IRentalsItemResponse) => [
    item.status === StatusesEnum.PUBLISHED && {
      key: 'website',
      label: (
        <a
          target='_blank'
          rel='noreferrer nofollow'
          href={`${b2bUrl}/rentals/${item.id}`}
        >
          На сайте
        </a>
      ),
    },
    {
      key: 'edit',
      label: <Link to={`/rentals/${item.id}/edit`}>Редактировать</Link>,
    },
    canBeUnPublished(item) && {
      key: 'unPublish',
      label: <Typography.Text>Снять с публикации</Typography.Text>,
      onClick: () => handleUnPublish(item.id),
    },
    {
      key: 'remove',
      label: <Typography.Text type='danger'>Удалить</Typography.Text>,
      onClick: () => {
        setModalIsOpen('remove');
        setCurrentItem(item);
      },
    },
  ];

  return (
    <MainLayout
      pageTitle={PageTitles.rentals}
      extraButtonsList={[
        <Link key={1} to='/rentals/add'>
          <Button type='primary'>Создать</Button>
        </Link>,
      ]}
      aside={<Filters />}
    >
      <Content>
        <ListTable
          data={list}
          total={total}
          getActions={getActions}
          limit={limit}
          setSort={setSort}
        />
      </Content>
      {currentItem?.id && (
        <>
          {modalIsOpen === 'remove' && (
            <StickyConfirmModal
              isOpen={modalIsOpen === 'remove'}
              onRequestClose={modalCloseHandler}
              title='Удаление карточки объекта аренды'
              okText='Удалить'
              text={
                <div>
                  Вы хотите убрать с портала и переместить в Корзину объект
                  аренды{' '}
                  <Typography.Text strong>
                    {currentItem.name || ''}
                  </Typography.Text>
                  ?
                </div>
              }
              onSubmit={() => handleDelete(currentItem?.id)}
              okButtonProps={{ danger: true }}
            />
          )}
        </>
      )}
    </MainLayout>
  );
};
